import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type { Invitation } from '../types/invitation'

export function useMyInvitations() {
  return useQuery<Invitation[]>({
    queryKey: ['invitations', 'mine'],
    queryFn: async () => {
      const res = await api.get('/invitations/mine')
      return res.data
    },
  })
}

export function useDemandInvitations(demandId: number | undefined) {
  return useQuery<Invitation[]>({
    queryKey: ['invitations', 'demand', demandId],
    queryFn: async () => {
      const res = await api.get(`/invitations/demand/${demandId}`)
      return res.data
    },
    enabled: !!demandId,
  })
}

export function useRespondToInvitation() {
  const qc = useQueryClient()
  return useMutation<
    Invitation,
    Error,
    { invitationId: number; action: 'accept' | 'decline'; coverLetter?: string; proposedFocus?: string; proposedHoursPerWeek?: number; proposedDuration?: string }
  >({
    mutationFn: async ({ invitationId, action, coverLetter, proposedFocus, proposedHoursPerWeek, proposedDuration }) => {
      const res = await api.patch(`/invitations/${invitationId}/respond`, {
        action,
        cover_letter: coverLetter,
        proposed_focus: proposedFocus,
        proposed_hours_per_week: proposedHoursPerWeek,
        proposed_duration: proposedDuration,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] })
      qc.invalidateQueries({ queryKey: ['applications'] })
    },
  })
}

export function useWithdrawInvitation() {
  const qc = useQueryClient()
  return useMutation<{ id: number; status: string }, Error, number>({
    mutationFn: async (invitationId) => {
      const res = await api.delete(`/invitations/${invitationId}`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] })
    },
    onError: () => {
      // Refresh invitation list on error (e.g. stale status)
      qc.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}
